import {
    ItecGridColsDef as GridColsDef,
    ItecColFilters,
    ItecDataGrid,
    useItecDataGrid
} from 'src/lib/itaptec/datagrid/src'
import { UserDTOFields, UserFilterDTOFields, UserViewDTO, UserViewDTOFields } from 'src/types/user/userData'

// import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid'
import CachedIcon from '@mui/icons-material/Cached'
import GroupsIcon from '@mui/icons-material/Groups'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import { FilterAltOff, Search } from '@mui/icons-material'
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Collapse,
    Dialog,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip
} from '@mui/material'
import { GridActionsCellItem, GridSearchIcon } from '@mui/x-data-grid'
import { useState } from 'react'
import toast from 'react-hot-toast'
import BlockUserIcon from 'src/components/icons/BlockUserIcon'
import UnblockUserIcon from 'src/components/icons/UnblockUserIcon'
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog'
import { HttpUser } from 'src/http/user/httpUser'
import { EntityWithIdFields, schemaSelectionOption } from 'src/types'
import { daysToNaturalLanguage } from 'src/utils/daysToNaturalLanguage'
import { z } from 'zod'
import { Controller } from 'react-hook-form'
import { ControlledDateRangePicker } from 'src/components/forms/ControlledDateRangePicker'
import { ICON_MAS } from '../../../../sistema/components/SharedIcons'
import { WrapperTable } from '../components'
import EditUser from './components/EditUser'
import EditUserGroups from './components/EditUserGroups'

const COL_NAMES = {
    Id: EntityWithIdFields.Id,
    Cargo: UserViewDTOFields.UserPositionDesc,
    Usuario: 'nombre_apellido',
    Email: UserDTOFields.Email,
    UltimoAcceso: UserViewDTOFields.LastLoginDate,
    Activo: UserViewDTOFields.Active,
    Bloqueado: UserViewDTOFields.Blocked
} as const

const schemaFiltroUsuarios = z.object({
    [UserViewDTOFields.Active]: z.array(schemaSelectionOption).optional(),
    [UserViewDTOFields.Blocked]: z.array(schemaSelectionOption).optional(),
    [UserDTOFields.Email]: z.string().optional(),
    [UserFilterDTOFields.UserDescription]: z.string().optional(),
    [UserDTOFields.UserPositionCode]: schemaSelectionOption.optional(),
    [UserFilterDTOFields.LastLoginDateStart]: z.date().optional(),
    [UserFilterDTOFields.LastLoginDateEnd]: z.date().optional()
})

type SchemaFiltroUsuariosType = z.infer<typeof schemaFiltroUsuarios>

const opcionActivo = { value: 1, label: 'Activos', color: 'success' }
const opcionTodos = { value: 2, label: 'Todos', color: 'info' }
const opcionBloqueado = { value: 1, label: 'Bloqueados', color: 'error' }
const opcionTodosBloqueado = { value: 2, label: 'Todos', color: 'info' }

const toggleActivoOptions: any[] = [opcionActivo, opcionTodos]

const toggleBloqueadoOptions: any[] = [opcionBloqueado, opcionTodosBloqueado]

const defaultFilterValues: Partial<SchemaFiltroUsuariosType> = {
    [UserViewDTOFields.Active]: [opcionActivo]
}

// Filtros dummy para que el hook cree el form reactivo (no se renderizan en headers)
const filtros: ItecColFilters<SchemaFiltroUsuariosType> = {
    [COL_NAMES.Usuario]: {
        type: 'text',
        name: UserFilterDTOFields.UserDescription,
        props: { debounceTime: 500, size: 'small' }
    },
    [COL_NAMES.Email]: {
        type: 'text',
        name: UserDTOFields.Email,
        props: { debounceTime: 500, size: 'small' }
    },
    [COL_NAMES.UltimoAcceso]: {
        type: 'date-range',
        name: UserFilterDTOFields.LastLoginDateStart,
        props: {
            startDateName: UserFilterDTOFields.LastLoginDateStart,
            endDateName: UserFilterDTOFields.LastLoginDateEnd
        }
    },
    [COL_NAMES.Activo]: {
        type: 'multiple-button-group',
        name: UserViewDTOFields.Active,
        props: { options: toggleActivoOptions, enforceValueSet: false, size: 'small' }
    },
    [COL_NAMES.Bloqueado]: {
        type: 'multiple-button-group',
        name: UserViewDTOFields.Blocked,
        props: { options: toggleBloqueadoOptions, enforceValueSet: false, size: 'small' }
    }
}

function TableUsers() {
    const { askConfirmation } = useConfirmationDialog()
    const [filtersOpen, setFiltersOpen] = useState(true)

    const getUserDisplayName = (user: UserViewDTO) =>
        `${user[UserDTOFields.FirstName] ?? ''} ${user[UserDTOFields.LastName] ?? ''}`.trim() ||
        user[UserDTOFields.Email] ||
        'este usuario'

    const columnas: GridColsDef<UserViewDTO>[] = [
        {
            headerName: 'Usuario',
            field: COL_NAMES.Usuario,
            valueGetter: ({ row }) => `${row[UserDTOFields.FirstName]} ${row[UserDTOFields.LastName]}`,
            renderCell: ({ row }) => {
                const nombre = `${row[UserDTOFields.FirstName] ?? ''} ${row[UserDTOFields.LastName] ?? ''}`.trim()
                const iniciales = `${row[UserDTOFields.FirstName]?.[0] ?? ''}${
                    row[UserDTOFields.LastName]?.[0] ?? ''
                }`.toUpperCase()

                return (
                    <Stack direction='row' alignItems='center' spacing={1.5} sx={{ minWidth: 0 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main' }}>
                            {iniciales || '?'}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.25 }}>
                                {nombre || 'Sin nombre'}
                            </Box>
                            <Box
                                sx={{
                                    fontSize: 12,
                                    color: 'text.secondary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {row[UserViewDTOFields.UserPositionDesc] || 'Sin posicion'}
                            </Box>
                        </Box>
                    </Stack>
                )
            },
            flex: 4
        },
        {
            headerName: 'Email',
            field: COL_NAMES.Email,
            flex: 3
        },
        {
            headerName: 'Último acceso',
            field: COL_NAMES.UltimoAcceso,
            valueFormatter: ({ value }) => (value ? daysToNaturalLanguage(new Date(value)) : ''),
            valueGetter: ({ value }) => (value ? new Date(value) : null),
            type: 'date',
            flex: 2
        },
        {
            headerName: 'Activo',
            field: COL_NAMES.Activo,
            type: 'boolean',
            flex: 0,
            minWidth: 118,
            renderCell: ({ value }) => (
                <Chip
                    size='small'
                    label={value ? 'Activo' : 'Inactivo'}
                    color={value ? 'success' : 'default'}
                    variant={value ? 'filled' : 'outlined'}
                />
            )
        },
        {
            headerName: 'Bloqueado',
            field: COL_NAMES.Bloqueado,
            type: 'boolean',
            flex: 0,
            minWidth: 126,
            renderCell: ({ value }) => (
                <Chip
                    size='small'
                    label={value ? 'Bloqueado' : 'Habilitado'}
                    color={value ? 'error' : 'info'}
                    variant={value ? 'filled' : 'outlined'}
                />
            )
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Acciones',
            align: 'center',
            headerAlign: 'center',
            minWidth: 168,
            getActions: ({ row }) => getActions(row, false)
        }
    ]

    const [selectedUserEdit, setSelectedUserEdit] = useState<UserViewDTO>()
    const [selectedUserGroups, setSelectedUserGroups] = useState<UserViewDTO>()

    const getActions = (user: UserViewDTO, small: boolean) => [
        user[UserViewDTOFields.Blocked] ? (
            <GridActionsCellItem
                key={2}
                onClick={() =>
                    askConfirmation(() => {
                        const promise = HttpUser.unBlockUser(user.id)
                        toast
                            .promise(promise, {
                                success: 'Habilitado',
                                error: 'No se pudo habilitar',
                                loading: 'Habilitando'
                            })
                            .then(() => refetch())
                    })
                }
                icon={
                    <Tooltip title='Desbloquear'>
                        <UnblockUserIcon color='success' />
                    </Tooltip>
                }
                label='Desbloquear'
                showInMenu={small}
            />
        ) : (
            <GridActionsCellItem
                key={2}
                onClick={() =>
                    askConfirmation(() => {
                        const promise = HttpUser.blockUser(user.id)
                        toast
                            .promise(promise, {
                                success: 'Bloqueado',
                                error: 'No se pudo bloquear',
                                loading: 'Bloqueando'
                            })
                            .then(() => refetch())
                    }, `Esta seguro que desea bloquear a ${getUserDisplayName(user)}?`)
                }
                icon={
                    <Tooltip title='Bloquear'>
                        <BlockUserIcon color='error' />
                    </Tooltip>
                }
                label='Bloquear'
                showInMenu={small}
            />
        ),
        user[UserViewDTOFields.Active] ? (
            <GridActionsCellItem
                key={3}
                onClick={() =>
                    askConfirmation(() => {
                        const promise = HttpUser.deactivateUser(user.id)

                        toast
                            .promise(promise, {
                                success: 'Inactivado',
                                error: 'No se pudo inactivar',
                                loading: 'Inactivando'
                            })
                            .then(() => refetch())
                    }, `Esta seguro que desea inactivar a ${getUserDisplayName(user)}?`)
                }
                icon={
                    <Tooltip title='Inactivar'>
                        <PersonRemoveIcon color='error' />
                    </Tooltip>
                }
                label='Inactivar'
                showInMenu={small}
            />
        ) : (
            <GridActionsCellItem
                key={3}
                onClick={() =>
                    askConfirmation(() => {
                        const promise = HttpUser.activateUser(user.id)

                        toast
                            .promise(promise, {
                                success: 'Activado',
                                error: 'No se pudo activar',
                                loading: 'Activando'
                            })
                            .then(() => refetch())
                    })
                }
                icon={
                    <Tooltip title='Activar'>
                        <PersonAddIcon color='success' />
                    </Tooltip>
                }
                label='Activar'
                showInMenu={small}
            />
        ),
        <GridActionsCellItem
            key={1}
            onClick={() => {
                setSelectedUserEdit(user)
            }}
            icon={
                <Tooltip title='Ver / Editar'>
                    <GridSearchIcon color='info' />
                </Tooltip>
            }
            label='Ver / Editar'
            showInMenu={small}
        />,
        <GridActionsCellItem
            key={4}
            onClick={() => {
                setSelectedUserGroups(user)
            }}
            icon={
                <Tooltip title='Grupos'>
                    <GroupsIcon color='primary' />
                </Tooltip>
            }
            label='Grupos'
            showInMenu={small}
        />
    ]

    const opcionUsuarioBloqueado = (filter: SchemaFiltroUsuariosType, field: keyof SchemaFiltroUsuariosType) => {
        const fieldValue = filter?.[field]
        if (Array.isArray(fieldValue) && fieldValue.length === 1) {
            if (fieldValue[0].value === opcionBloqueado.value) return true
            if (fieldValue[0].value === opcionTodosBloqueado.value) return undefined
        }

        return undefined
    }

    const opcionUsuarioActivo = (filter: SchemaFiltroUsuariosType, field: keyof SchemaFiltroUsuariosType) => {
        const fieldValue = filter?.[field]
        if (!fieldValue) return true
        if (Array.isArray(fieldValue) && fieldValue.length === 1) {
            if (fieldValue[0].value === opcionActivo.value) return true
            if (fieldValue[0].value === opcionTodos.value) return undefined
        }

        return undefined
    }

    const {
        datagridProps,
        control: {
            query: { refetch },
            form: filterForm
        }
    } = useItecDataGrid({
        pagination: true,
        columns: columnas,
        getEntitiesPaginated: (pagination, filter) =>
            HttpUser.getUserList(
                pagination,
                filter
                    ? {
                          [UserViewDTOFields.Active]: opcionUsuarioActivo(
                              { ...defaultFilterValues, ...filter },
                              UserViewDTOFields.Active
                          ),
                          [UserDTOFields.Email]: filter[UserDTOFields.Email],
                          [UserFilterDTOFields.UserDescription]: filter[UserFilterDTOFields.UserDescription],
                          [UserDTOFields.UserPositionCode]: filter[UserDTOFields.UserPositionCode]?.value,
                          [UserFilterDTOFields.LastLoginDateStart]: filter[UserFilterDTOFields.LastLoginDateStart],
                          [UserFilterDTOFields.LastLoginDateEnd]: filter[UserFilterDTOFields.LastLoginDateEnd],
                          [UserViewDTOFields.Blocked]: opcionUsuarioBloqueado(filter, UserViewDTOFields.Blocked)
                      }
                    : {
                          [UserViewDTOFields.Active]: true
                      }
            ),
        density: 'standard',
        filters: filtros
    })

    const cleanDatagridProps = {
        ...datagridProps,
        columns: datagridProps.columns.map(col => ({
            ...col,
            disableColumnMenu: true,
            renderHeader: undefined
        })),
        slots: undefined
    }

    const handleClearFilters = () => {
        if (!filterForm) return
        filterForm.reset({
            [UserViewDTOFields.Active]: [opcionActivo],
            [UserViewDTOFields.Blocked]: undefined,
            [UserDTOFields.Email]: undefined,
            [UserFilterDTOFields.UserDescription]: undefined,
            [UserDTOFields.UserPositionCode]: undefined,
            [UserFilterDTOFields.LastLoginDateStart]: undefined,
            [UserFilterDTOFields.LastLoginDateEnd]: undefined
        })
    }

    return (
        <WrapperTable>
            <Card
                elevation={0}
                sx={{
                    border: theme => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    overflow: 'hidden'
                }}
            >
                <CardHeader
                    sx={{
                        px: 3,
                        py: 2.5,
                        '& .MuiCardHeader-title': { fontWeight: 700 },
                        '& .MuiCardHeader-subheader': { mt: 0.5 }
                    }}
                    title='Administracion de usuarios'
                    subheader='Gestiona usuarios, estado de acceso y grupos asociados'
                    action={
                        <Stack direction='row' alignItems='center' spacing={1}>
                            <Tooltip title={filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'}>
                                <IconButton onClick={() => setFiltersOpen(prev => !prev)} size='small'>
                                    <FilterAltIcon color={filtersOpen ? 'primary' : 'inherit'} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title='Actualizar'>
                                <span>
                                    <IconButton disabled={datagridProps.loading} onClick={() => refetch()} size='small'>
                                        <CachedIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={ICON_MAS}
                                onClick={() => {
                                    setSelectedUserEdit({
                                        [EntityWithIdFields.Id]: 0
                                    } as UserViewDTO)
                                }}
                            >
                                Nuevo
                            </Button>
                        </Stack>
                    }
                />

                {/* Barra de filtros colapsable */}
                <Collapse in={filtersOpen}>
                    {filterForm && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2,
                                alignItems: 'center',
                                mx: 3,
                                mb: 2,
                                p: 2,
                                borderRadius: 1,
                                border: theme => `1px solid ${theme.palette.divider}`,
                                bgcolor: 'background.default'
                            }}
                        >
                            <Controller
                                control={filterForm.control}
                                name={UserFilterDTOFields.UserDescription}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        value={field.value ?? ''}
                                        label='Usuario'
                                        size='small'
                                        sx={{ minWidth: 210, flex: 1 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='start'>
                                                    <Search fontSize='small' />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                control={filterForm.control}
                                name={UserDTOFields.Email}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        value={field.value ?? ''}
                                        label='Email'
                                        size='small'
                                        sx={{ minWidth: 230, flex: 1 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='start'>
                                                    <Search fontSize='small' />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                )}
                            />
                            <ControlledDateRangePicker
                                control={filterForm.control}
                                startDateName={UserFilterDTOFields.LastLoginDateStart}
                                endDateName={UserFilterDTOFields.LastLoginDateEnd}
                                label='Ultimo acceso'
                                sx={{ minWidth: 280, flex: 1 }}
                            />
                            <Controller
                                control={filterForm.control}
                                name={UserViewDTOFields.Active}
                                render={({ field }) => (
                                    <ToggleButtonGroup
                                        size='small'
                                        exclusive
                                        value={(field.value ?? [opcionActivo])[0]?.value ?? null}
                                        onChange={(_, newValue: number | null) => {
                                            if (newValue === null) return
                                            const option = toggleActivoOptions.find(o => o.value === newValue)
                                            field.onChange(option ? [option] : [])
                                        }}
                                    >
                                        {toggleActivoOptions.map(option => (
                                            <ToggleButton key={option.value} value={option.value} color={option.color}>
                                                {option.label}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                )}
                            />
                            <Controller
                                control={filterForm.control}
                                name={UserViewDTOFields.Blocked}
                                render={({ field }) => (
                                    <ToggleButtonGroup
                                        size='small'
                                        exclusive
                                        value={(field.value ?? [])[0]?.value ?? null}
                                        onChange={(_, newValue: number | null) => {
                                            if (newValue === null) return
                                            const option = toggleBloqueadoOptions.find(o => o.value === newValue)
                                            field.onChange(option ? [option] : [])
                                        }}
                                    >
                                        {toggleBloqueadoOptions.map(option => (
                                            <ToggleButton key={option.value} value={option.value} color={option.color}>
                                                {option.label}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                )}
                            />
                            <Tooltip title='Limpiar filtros'>
                                <IconButton size='small' onClick={handleClearFilters}>
                                    <FilterAltOff fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Collapse>

                <CardContent sx={{ pt: 0, px: 3, pb: 3 }}>
                    <Box
                        sx={{
                            '& .MuiDataGrid-root': {
                                border: theme => `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                overflow: 'hidden'
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: 'background.default'
                            },
                            '& .MuiDataGrid-row:hover': {
                                bgcolor: 'action.hover'
                            }
                        }}
                    >
                        <ItecDataGrid {...cleanDatagridProps} />
                    </Box>
                </CardContent>
            </Card>
            <>
                <Dialog open={!!selectedUserEdit} fullWidth maxWidth='sm' keepMounted={false}>
                    {selectedUserEdit && (
                        <EditUser
                            mode={selectedUserEdit.id === 0 ? 'edit' : 'view'}
                            onClose={() => {
                                setSelectedUserEdit(undefined)
                                refetch()
                            }}
                            user={selectedUserEdit}
                        />
                    )}
                </Dialog>
                <Dialog open={!!selectedUserGroups} fullWidth maxWidth='xl' keepMounted={false}>
                    {selectedUserGroups && (
                        <EditUserGroups
                            onClose={() => {
                                setSelectedUserGroups(undefined)
                                refetch()
                            }}
                            user={selectedUserGroups}
                        />
                    )}
                </Dialog>
            </>
        </WrapperTable>
    )
}

export default TableUsers
